'use client';

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Spinner } from "@/components/ui/spinner";
import { ArrowRight } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";


interface ChatInterfaceProps {
    sendMessage: (content: string) => void;
    isLoading: boolean;
    isFrontpage: boolean;
}

const PlaceHolders = [
    "Ask me about my work...",
    "Ask me about what I build...",
    "Ask how I manage teams...",
    "Ask me about my career...",
    "Ask about my tech stack...",
]

export default function ChatInterface({ sendMessage, isLoading, isFrontpage }: ChatInterfaceProps) {

    const [input, setInput] = useState('');
    const [placeholder, setPlaceholder] = useState('');
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // Track the current rotation task to kill it if needed
    const rotationRef = useRef<gsap.core.Tween | null>(null);

    useGSAP(() => {
        // Cleanup function to kill any ongoing rotation
        const cleanup = () => {
            if (rotationRef.current) {
                rotationRef.current.kill();
                rotationRef.current = null;
            }
        };
        cleanup();

        if (containerRef.current) {

            if (!isFrontpage) {
                // Hard stop: immediately set visible static state
                gsap.set(containerRef.current, { opacity: 1, left: "0%", width: "100%" });
                setPlaceholder(PlaceHolders[0]);
                return;
            }

            const tl = gsap.timeline();

            // Random start if on frontpage
            let currentText = PlaceHolders[Math.floor(Math.random() * PlaceHolders.length)];
            const chars = { val: 0 };

            // Initial reveal sequence
            tl.fromTo(containerRef.current,
                {
                    opacity: 0,
                    left: "50%",
                    width: "0%"
                },
                {
                    opacity: 1,
                    left: "0%",
                    width: "100%",
                    duration: .5,
                    delay: 0.5,
                    ease: "power3.out",
                }
            ).to(chars, {
                val: currentText.length,
                duration: .5,
                ease: "none",
                onUpdate: () => {
                    setPlaceholder(currentText.substring(0, Math.ceil(chars.val)));
                },
            });

            // Start the rotation loop after the intro
            tl.call(() => {
                // Recursive loop function
                const nextLoop = () => {
                    // Store the delayedCall so it gets killed if effect re-runs (cleanup)
                    // Wait 3 seconds before deleting
                    rotationRef.current = gsap.delayedCall(3, () => {
                        // Delete text
                        gsap.to(chars, {
                            val: 0,
                            duration: 0.5,
                            ease: "none",
                            onUpdate: () => {
                                setPlaceholder(currentText.substring(0, Math.ceil(chars.val)));
                            },
                            onComplete: () => {
                                // Pick new unique text
                                let nextText = currentText;
                                while (nextText === currentText) {
                                    nextText = PlaceHolders[Math.floor(Math.random() * PlaceHolders.length)];
                                }
                                currentText = nextText;

                                // Type new text
                                gsap.to(chars, {
                                    val: currentText.length,
                                    duration: 0.5,
                                    ease: "none",
                                    onUpdate: () => {
                                        setPlaceholder(currentText.substring(0, Math.ceil(chars.val)));
                                    },
                                    onComplete: () => {
                                        // Schedule next loop
                                        nextLoop();
                                    }
                                });
                            }
                        });
                    });
                };

                nextLoop();
            });
        }

        return cleanup;
    }, { scope: containerRef, dependencies: [isFrontpage] });

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit(e as React.FormEvent);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!input.trim() || isLoading) return;

        const userMessage = input.trim();
        setInput('');
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
        }

        sendMessage(userMessage);
    };

    return (
        <div className="md:pb-4">
            <div className="p-4">
                <form onSubmit={handleSubmit} className="flex gap-2 items-end">
                    <div ref={containerRef} className="relative w-full opacity-0">
                        <Textarea
                            ref={textareaRef}
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder={placeholder}
                            className="flex-1 max-h-[200px] rounded-4xl text-lg! border-0! focus:ring-0! resize-none py-5 pl-7 pr-26 [&::-webkit-scrollbar]:hidden bg-input"
                            disabled={isLoading}
                            rows={1}
                            autoCorrect="off"
                            autoCapitalize="none"
                            autoComplete="off"
                            spellCheck={false}

                        />
                        <Button
                            type="submit"
                            disabled={isLoading || !input.trim()}
                            size="icon"
                            variant="secondary"
                            className="absolute bottom-3 right-4 p-0 w-10 h-10 rounded-full"
                        >
                            {isLoading ? <Spinner /> : <ArrowRight className="w-4 h-4" />}
                            <span className="sr-only">Send</span>
                        </Button>
                    </div>
                </form>
            </div>
        </div>

    );
}